import {StateController} from "@lit-app/state/src/state-controller";
import {html, HTMLTemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {takeUntil} from "rxjs/operators";
import {container} from "tsyringe";

import {appState} from "../../app.state";
import {AbstractLitElement} from "../../components/abstract-lit.element";
import "../../components/_forms/ha-form-knowledge-filters.component";
import "../../components/knowledge/knowledge-view-pane/knowledge-view-pane.component";
import {RxjsLifecycleController} from "../../components/_reactive-controllers/rxjs-lifecycle.controller";
import type {KnowledgeObject} from "../../domain/entities/knowledge-object.entity";
import {KnowledgeObjectRepository} from "../../domain/repositories/knowledge-object.repository";

/**
 * Top-level Knowledge overview view: the polymorphic `knowledge-view-pane`.
 *
 * Routes `/knowledge` and `/knowledge/:sid/:slug?`. The list/filter/create
 * sidebar content that used to live here moved to `<knowledge-sidebar-list>`
 * in `ha-app`'s persistent `<sidebar>` (operator request 2026-08-17) — this
 * view now only resolves the routed `sid` against
 * `KnowledgeObjectRepository.items$` (a singleton `BehaviorSubject` that
 * `knowledge-sidebar-list` also observes and is the one calling `reload()`,
 * so this is a passive read, no duplicate fetch) to pick which object the
 * pane shows. The settings/burger menu (operator request 2026-08-17, moved to
 * the sidebar for ALL board views, see ha-app.component.ts) stays gone, and
 * — as of 2026-08-30 (Stefan) — so is any toolbar of this view's own: the
 * filter-toggle button that briefly lived in a near-empty `.timeline-toolbar`
 * here moved into whichever toolbar the currently-open object type already
 * has (`knowledge-whiteboard`/`knowledge-rte`/`knowledge-view-pane`'s
 * image/empty-state fallback — see `knowledge-filter-toggle.helper.ts`), so
 * this view no longer renders a toolbar row of its own at all.
 * `ha-form-knowledge-filters` (type combobox + title search, previously
 * always-visible inline in `knowledge-sidebar-list`) lives in a
 * `.sidebar__filter` drawer here — same pattern `ha-tasklist.view.ts`/
 * `abstract-ha-timeline.view.ts` use for `ha-form-issue-filters`, reusing its
 * exact classes/markup so the existing CSS (`todo_sidebar.css`) applies
 * unchanged. `knowledge-sidebar-list` itself (the file list in the
 * persistent left sidebar) is untouched — only the filter FORM moved out of
 * it. Light DOM is inherited from AbstractLitElement (ADR-007).
 */
@customElement('knowledge-overview-view')
export class KnowledgeOverviewView extends AbstractLitElement {

    /** ShortId from the `/knowledge/:sid/:slug?` route param; selects an object. */
    @property({type: String})
    public sid?: string;

    @state()
    private _items: KnowledgeObject[] = [];

    /** Required by RxjsLifecycleController for reconnect-safe auto-cleanup. */
    public readonly rxjs = new RxjsLifecycleController(this);

    private readonly _repo: KnowledgeObjectRepository = container.resolve(KnowledgeObjectRepository);

    /** Re-renders on `appState.knowledgeFilterOpen` toggling — the filter-toggle
     *  button now lives inside `knowledge-view-pane`'s dispatched content (see
     *  class docblock), but the drawer's expanded/collapsed class is still
     *  read here — mirrors ha-tasklist.view.ts's ctor. */
    public constructor() {
        super();
        new StateController(this, appState);
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this._repo.items$
            .pipe(takeUntil(this.rxjs.disconnected$))
            .subscribe(items => {
                this._items = items;
            });
    }

    protected render(): HTMLTemplateResult {
        const selectedItem = this.sid
            ? this._items.find(ko => ko.uid === this.sid) ?? null
            : null;

        return html`
            <div class="view__body">
                <div class="view__column">
                    <knowledge-view-pane .item=${selectedItem}></knowledge-view-pane>
                </div>
                <div class="sidebar__filter ${appState.knowledgeFilterOpen ? 'expanded' : 'collapsed'}">
                    <main class="sidebar__filter__main">
                        <ha-form-knowledge-filters class="wrapper"></ha-form-knowledge-filters>
                    </main>
                </div>
            </div>
        `;
    }
}
